// src/components/forms/ViewingForm.tsx — book a viewing, or reschedule one.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useLeadOptions, useListingOptions, useUserOptions } from '../../lib/options';
import { Button, Field, Input, Modal, Select, Textarea } from '../ui';
import { FormError, FormGrid, Wide, nulled, numberOr } from './common';

type V = Record<string, any>;
const STATUSES = ['BOOKED', 'CONFIRMED', 'COMPLETED', 'NO_SHOW', 'CANCELLED'];
const label = (s: string) => s.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

/** datetime-local wants "2026-09-08T14:30" and gives it back the same way. */
const forInput = (iso?: string) => (iso ? new Date(iso).toISOString().slice(0, 16) : '');

export function ViewingForm({ open, onClose, viewing, leadId }: { open: boolean; onClose: () => void; viewing?: V; leadId?: string }) {
  const isNew = !viewing;
  const qc = useQueryClient();
  const listings = useListingOptions(isNew);
  const leads = useLeadOptions(isNew && !leadId);
  const users = useUserOptions();
  const [form, setForm] = useState(() => ({
    listingId: viewing?.listingId ?? '', leadId: viewing?.leadId ?? leadId ?? '',
    scheduledAt: forInput(viewing?.scheduledAt), durationMinutes: String(viewing?.durationMinutes ?? 30),
    agentId: viewing?.agentId ?? '', status: viewing?.status ?? 'BOOKED', feedback: viewing?.feedback ?? '',
  }));
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const save = useMutation({
    mutationFn: async () => (isNew
      ? (await api.post<V>('/viewings', {
          listingId: form.listingId, leadId: nulled(form.leadId),
          scheduledAt: new Date(form.scheduledAt).toISOString(),
          durationMinutes: numberOr(form.durationMinutes, 30), agentId: nulled(form.agentId),
        })).data
      : (await api.patch<V>(`/viewings/${viewing!.id}`, {
          scheduledAt: new Date(form.scheduledAt).toISOString(),
          durationMinutes: numberOr(form.durationMinutes, 30), agentId: nulled(form.agentId),
          status: form.status, feedback: nulled(form.feedback),
        })).data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['/viewings'] }); void qc.invalidateQueries({ queryKey: ['/leads'] }); onClose(); },
  });

  return (
    <Modal open={open} onClose={onClose} size="lg" title={isNew ? 'Book a viewing' : 'Viewing'}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={() => save.mutate()} disabled={!form.listingId || !form.scheduledAt || save.isPending}>{save.isPending ? 'Saving…' : isNew ? 'Book viewing' : 'Save changes'}</Button>
      </>}>
      <div className="space-y-5">
        <FormGrid>
          <Wide>
            <Field label="Home">
              {isNew
                ? <Select value={form.listingId} onChange={(e) => set({ listingId: e.target.value })} className="w-full"><option value="">Choose a listing…</option>{(listings.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.headline} · {l.property?.name}</option>)}</Select>
                : <Input value={`${viewing!.listingHeadline ?? ''}${viewing!.propertyName ? ` · ${viewing!.propertyName}` : ''}`} disabled className="w-full" />}
            </Field>
          </Wide>
          {isNew && !leadId && (
            <Wide><Field label="Lead" hint="Booking against a lead moves them to viewing booked"><Select value={form.leadId} onChange={(e) => set({ leadId: e.target.value })} className="w-full"><option value="">Not linked</option>{(leads.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.name ?? l.email ?? l.phone}</option>)}</Select></Field></Wide>
          )}

          <Field label="When"><Input type="datetime-local" value={form.scheduledAt} onChange={(e) => set({ scheduledAt: e.target.value })} className="w-full" /></Field>
          <Field label="Minutes"><Input value={form.durationMinutes} onChange={(e) => set({ durationMinutes: e.target.value })} inputMode="numeric" className="w-full" /></Field>
          <Field label="Agent"><Select value={form.agentId} onChange={(e) => set({ agentId: e.target.value })} className="w-full"><option value="">Unassigned</option>{(users.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}</Select></Field>

          {!isNew && <>
            <Field label="Status"><Select value={form.status} onChange={(e) => set({ status: e.target.value })} className="w-full">{STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}</Select></Field>
            <Wide><Field label="Feedback" hint="What they said about the home"><Textarea rows={3} value={form.feedback} onChange={(e) => set({ feedback: e.target.value })} className="w-full" /></Field></Wide>
          </>}
        </FormGrid>
        <FormError error={save.error} />
      </div>
    </Modal>
  );
}
